import React, { useEffect, useState } from "react";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  CircularProgress,
  Switch,
  Button,
  Input,
} from "@nextui-org/react";
import { FaChevronDown } from "react-icons/fa";
import { BsDatabaseFill, BsPlus } from "react-icons/bs";
import { LuChevronsUpDown, LuUserPlus, LuUsers, LuNetwork, LuMapPin } from "react-icons/lu";
import {
  MdOutlineCorporateFare,
  MdOutlineToggleOn,
  MdOutlineWorkspacePremium,
} from "react-icons/md";
import { IoSearch } from "react-icons/io5";
import { useTranslation } from "react-i18next";

import InvitationModal from "./InvitationModal";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import MEditableDisplay from "../../components/Kit/MEditableDisplay";
import PaginationTable from "../../components/PaginationTable";
import MHoverToggleMenu from "../../components/Kit/MHoverToggleMenu";
import FilterDropdown from "../../components/FilterDropdown";
import Modal from "../../components/Modal";

import getConfig from "../../conf/config";
import myAxios from "../../utils/axiosWrapper";
import { auth } from "../../conf/firebase";
import { MedalIcon1, MedalIcon2 } from "../../assets/icons/audio-icons";
import { getAdminSettingsTableColumns } from "../../constants/tableHeaders";
import { filterSortAndPaginateAdminSettingsOperators } from "../../utils/adminSettingsUtils";
import { useAppContext } from "../../contexts/AppContext";

const DEFAULT_FILTERS = {
  role: "all",
  departmentId: "all",
  teamId: "all",
  localisationId: "all",
  status: "all",
};

const MembersTab = ({ company_config, organisations, refreshOrganisations }) => {
  const { currentUser } = auth;
  const { t } = useTranslation();

  const { isInviteMemberModalDisplayed, setIsInviteMemberModalDisplayed } = useAppContext();
  const disableInvitationModal = company_config?.feature_flags?.hide_invitation_btn || false;

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const [creationInputName, setCreationInputName] = useState("");
  const [operators, setOperators] = useState([]);
  const [operatorMap, setOperatorMap] = useState({});
  const [extensionsCXProviderInputsData, setExtensionsCXProviderInputsData] = useState({});
  const [isOperatorsLoading, setIsOperatorsLoading] = useState(false);
  const [selectedOperatorForInvite, setSelectedOperatorForInvite] = useState(null);
  const [cellLoader, setCellLoader] = useState({
    rowId: null,
    cellName: null,
    isLoading: false,
  });

  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "name",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [filteredOperatorsCount, setFilteredOperatorsCount] = useState(0);

  const is3cx = company_config?.cx_provider === "3cx";
  const isAircall = company_config?.cx_provider === "aircall";
  const isSftpHsf = company_config?.cx_provider === "sftp_hsf";

  const onResetCellLoader = () => {
    setCellLoader({
      rowId: null,
      cellName: null,
      isLoading: false,
    });
  };

  const onInviteExistingOperator = (operator) => {
    setSelectedOperatorForInvite(operator);
    setIsInviteMemberModalDisplayed(true);
  };

  useEffect(() => {
    const { operators: nextFilteredOperators, nbFiltered } =
      filterSortAndPaginateAdminSettingsOperators(
        operators,
        searchText,
        sortDescriptor,
        rowsPerPage,
        page - 1,
        filters
      );

    setFilteredOperators(nextFilteredOperators);
    setFilteredOperatorsCount(nbFiltered);
  }, [operators, searchText, sortDescriptor, rowsPerPage, page, filters]);

  useEffect(() => {
    setPage(1);
  }, [searchText, filters]);

  const fetchOperators = async () => {
    setIsOperatorsLoading(true);
    const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/admin/operators`);
    const retrievedOperators = response?.data?.[0]?.operators || {};

    setOperatorMap(retrievedOperators);
    setIsOperatorsLoading(false);
    onResetCellLoader();
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    fetchOperators();
  }, [currentUser]);

  useEffect(() => {
    const formattedOperators = [];
    const extensionsInputsMap = {};

    Object.keys(operatorMap).forEach((operatorId) => {
      if (operatorId === "no_operator_id") {
        operatorMap[operatorId].forEach((userWithoutOperator) => {
          formattedOperators.push({
            ...userWithoutOperator,
            user: userWithoutOperator,
            noOperatorId: true,
          });
        });
      } else {
        formattedOperators.push(operatorMap[operatorId]);
        if (is3cx) {
          extensionsInputsMap[operatorId] = operatorMap[operatorId]?.extension_3cx || -1;
        }
        if (isAircall) {
          extensionsInputsMap[operatorId] = operatorMap[operatorId]?.extension_aircall || -1;
        }
        if (isSftpHsf) {
          extensionsInputsMap[operatorId] = operatorMap[operatorId]?.cx_one_identifier || "";
        }
      }
    });

    setExtensionsCXProviderInputsData(extensionsInputsMap);
    setOperators(formattedOperators);
  }, [operatorMap, company_config]);

  const onUpdateOperator =
    (operatorId) =>
    async (updates, withFetch = true) => {
      await myAxios.put(`${getConfig().API_URL}/audio/api/v1/admin/operator/${operatorId}`, {
        ...updates,
      });
      if (withFetch) {
        fetchOperators();
      }
    };

  const onUpdateUserRole = (userId) => async (role) => {
    await myAxios.put(`${getConfig().API_URL}/audio/api/v1/admin/user_role/${userId}`, {
      role,
    });
    fetchOperators();
  };

  const onUpdateOperatorOrgLocation = (operatorId, orgKey, newId) => {
    setCellLoader({
      rowId: operatorId,
      cellName: orgKey,
      isLoading: true,
    });
    const orgOptionsByKey = {
      department: organisations?.departments || [],
      localisation: organisations?.localisations || [],
      team: organisations?.teams || [],
    };

    const orgList = orgOptionsByKey[orgKey];
    const orgItem = orgList.find((item) => item.id === newId);

    const updateObjectByOrg = {
      department: {
        department_id: orgItem.id,
        department_name: orgItem.name,
      },
      localisation: {
        localisation_id: orgItem.id,
        localisation_name: orgItem.name,
      },
      team: {
        team_id: orgItem.id,
        team_name: orgItem.name,
      },
    };

    onUpdateOperator(operatorId)(updateObjectByOrg[orgKey]);
  };

  const onCreateOrgItem = (orgKey) => async () => {
    if (creationInputName?.length <= 0) {
      return;
    }
    await myAxios.post(`${getConfig().API_URL}/audio/api/v1/admin/create_org_item`, {
      type: orgKey,
      name: creationInputName,
    });
    setCreationInputName("");
    refreshOrganisations();
  };

  const onToggleActiveOperator = (operator) => async () => {
    setCellLoader({
      rowId: operator?.id,
      cellName: "active",
      isLoading: true,
    });

    await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/admin/update_operator_activity/${operator.id}`,
      {
        is_active: !operator?.is_active,
      }
    );
    fetchOperators();
    onResetCellLoader();
  };

  const setExtensionInput = (operatorId) => async (nextExtensionInput) => {
    const nextExtensionsInputObject = { ...extensionsCXProviderInputsData };
    nextExtensionsInputObject[operatorId] = nextExtensionInput;

    if (isSftpHsf) {
      await onUpdateOperator(operatorId)({ cx_one_identifier: nextExtensionInput });
    } else {
      await onUpdateOperator(operatorId)({ extension_3cx: parseInt(nextExtensionInput) });
    }

    setExtensionsCXProviderInputsData(nextExtensionsInputObject);
  };

  const renderRole = (operator) => {
    const hasUser = operator?.user !== null;
    const hasOperatorId = !operator?.noOperatorId;
    const isActive = operator?.is_active;
    const isOperatorSuperAdmin = (operator?.user?.permissions || []).includes("SUPER_ADMIN");

    const memberDropdownOptions = [
      {
        label: "Member",
        key: "member",
      },
      {
        label: "Admin",
        key: "admin",
      },
    ];

    if (!hasUser && hasOperatorId && isActive) {
      return (
        <Button
          size="sm"
          color="primary"
          variant="flat"
          onPress={() => onInviteExistingOperator(operator)}
          className="text-xs"
        >
          {t("Invite")}
        </Button>
      );
    }

    return hasUser ? (
      <Dropdown>
        <DropdownTrigger>
          <div
            className={`text-sm flex items-center rounded-lg px-[12px] py-[4px] ${
              isOperatorSuperAdmin ? "bg-[#4318FF]/[.18]" : "border-solid border-1 border-[#E5E5E5]"
            }`}
          >
            {isOperatorSuperAdmin && <MedalIcon1 size={20} />}
            {!isOperatorSuperAdmin && <MedalIcon2 size={20} />}
            <span className={`mr-[4px] ${isOperatorSuperAdmin ? "text-[#4318FF]" : ""}`}>
              {isOperatorSuperAdmin ? "Admin" : "Member"}
            </span>
            <FaChevronDown color={isOperatorSuperAdmin ? "#4318FF" : "#737373"} />
          </div>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Actions"
          onAction={(key) => {
            onUpdateUserRole(operator?.user?.id)(key);
          }}
        >
          {memberDropdownOptions.map((dropdownOption) => (
            <DropdownItem key={dropdownOption.key}>{dropdownOption.label}</DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    ) : (
      <span className="text-xs text-[#404040]/[0.3]">-</span>
    );
  };

  const renderOrgCell = (operator, orgKey) => {
    const orgOptionsByKey = {
      department: (organisations?.departments || [])?.map((dpt) => ({
        label: dpt.name,
        key: dpt.id,
      })),
      localisation: (organisations?.localisations || [])?.map((loc) => ({
        label: loc.name,
        key: loc.id,
      })),
      team: (organisations?.teams || [])?.map((team) => ({
        label: team.name,
        key: team.id,
      })),
    };

    const labelToDisplayByOrgKey = {
      department: "department_name",
      localisation: "localisation_name",
      team: "team_name",
    };

    const creationLabel = {
      department: "Create a new department",
      localisation: "Create a new localisation",
      team: "Create a new team",
    };

    const selectedKeyplayByOrgKey = {
      department: "department_id",
      localisation: "localisation_id",
      team: "team_id",
    };

    const orgDropdownOptions = orgOptionsByKey[orgKey];
    const orgLabelToDisplay = labelToDisplayByOrgKey[orgKey];
    const selectedKey = operator[selectedKeyplayByOrgKey[orgKey]];

    orgDropdownOptions.sort((a, b) => {
      const cellValueA = a.label || "";
      const cellValueB = b.label || "";
      return cellValueA.localeCompare(cellValueB);
    });

    return (
      <MHoverToggleMenu
        wrapperClassNames="w-[80%]"
        floatingWrapperClassNames="w-[100%]"
        triggerComponent={
          <div className="cursor-pointer shadow-custom-light border-solid border-1 border-[#E5E5E5] rounded-md px-[12px] py-[8px] bg-white flex items-center justify-between">
            <span className="text-[14px] text-[#171717]">
              {operator[orgLabelToDisplay] || "None"}
            </span>
            <LuChevronsUpDown
              color="#171717"
              size={12}
            />
          </div>
        }
        items={orgDropdownOptions}
        selectedId={selectedKey}
        setSelectedId={(key) => {
          onUpdateOperatorOrgLocation(operator.id, orgKey, key);
        }}
        customRender={
          <div>
            <div className="flex items-center mb-2 text-sm">
              <BsPlus size={24} />
              <span className="whitespace-nowrap ml-1 mr-4">{t(creationLabel[orgKey])}</span>
            </div>
            <div className="flex items-center rounded-md border-2 border-[#E5E5E5] border-solid bg-white pl-2 text-sm">
              <input
                className="outline-none border-0 grow"
                placeholder={t("Name")}
                value={creationInputName}
                onChange={(e) => setCreationInputName(e.target.value)}
              />
              <div
                onClick={onCreateOrgItem(orgKey)}
                onKeyDown={onCreateOrgItem(orgKey)}
                className="cursor-pointer rounded-md border-2 border-[#E5E5E5] border-solid px-3 py-2"
                style={{ margin: "-2px" }}
              >
                {t("Create")}
              </div>
            </div>
          </div>
        }
      />
    );
  };

  const renderLoading = () => (
    <CircularProgress
      size="sm"
      aria-label="Loading..."
      color="primary"
    />
  );

  const renderCell = (item, columnKey) => {
    const isCellLoading =
      cellLoader?.isLoading && cellLoader?.rowId === item?.id && cellLoader?.cellName === columnKey;

    if (isCellLoading) {
      return renderLoading();
    }

    const hasExtension =
      (is3cx && (item?.extension_3cx || null) !== null) ||
      (isAircall && (item?.extension_aircall || null) !== null) ||
      (isSftpHsf && !item?.noOperatorId);
    const extensionInput = hasExtension ? extensionsCXProviderInputsData?.[item.id] ?? "" : "";
    const hasOperatorId = item?.noOperatorId;

    switch (columnKey) {
      case "name":
        return (
          <ACircleUserDisplay
            firstname={item.first_name}
            lastname={item.last_name}
            email={item.email}
          />
        );
      case "role":
        return renderRole(item);
      case "department":
      case "team":
      case "localisation":
        return hasOperatorId ? (
          <span className="text-xs text-[#404040]/[0.3]">-</span>
        ) : (
          renderOrgCell(item, columnKey)
        );
      case "extension_3cx":
        return (
          <div className="w-full">
            {hasExtension && (
              <MEditableDisplay
                type="number"
                placeholder="3CX"
                label={extensionInput}
                onChange={(newData) => setExtensionInput(item.id)(newData)}
              />
            )}
            {!hasExtension && <span className="text-xs text-[#404040]/[0.3]">-</span>}
          </div>
        );
      case "extension_aircall":
        return (
          <div className="w-full">
            {hasExtension && <span className="text-xs text-[#404040]">{extensionInput}</span>}
            {!hasExtension && <span className="text-xs text-[#404040]/[0.3]">-</span>}
          </div>
        );
      case "cx_one_identifier":
        return (
          <div className="w-full">
            {hasExtension && (
              <MEditableDisplay
                type="text"
                placeholder="CX One ID"
                label={extensionInput}
                onChange={(newData) => setExtensionInput(item.id)(newData)}
              />
            )}
            {!hasExtension && <span className="text-xs text-[#404040]/[0.3]">-</span>}
          </div>
        );
      case "active":
        if (hasOperatorId) {
          return;
        }

        return (
          <div className="flex items-center justify-end grow">
            <Switch
              size="sm"
              isSelected={item?.is_active}
              onValueChange={onToggleActiveOperator(item)}
            />
          </div>
        );
      default:
        return <div />;
    }
  };

  const renderTableEmptyState = () => {
    return (
      <div className="w-full h-full flex-1 flex flex-col items-center justify-center bg-white rounded-3xl py-12">
        <BsDatabaseFill className="text-gray-300 w-12 h-12" />
        <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
      </div>
    );
  };

  const toOrgFilterOptions = (orgItems, allLabel) => [
    { key: "all", label: allLabel },
    ...(orgItems || [])
      .map((item) => ({ key: item.id, label: item.name }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || "")),
  ];

  const roleOptions = [
    { key: "all", label: "All roles" },
    { key: "admin", label: "Admin" },
    { key: "member", label: "Member" },
    { key: "no_account", label: "No account" },
  ];

  const statusOptions = [
    { key: "all", label: "All statuses" },
    { key: "active", label: "Active" },
    { key: "inactive", label: "Inactive" },
  ];

  const setFilter = (filterKey) => (value) => {
    setFilters((previous) => ({ ...previous, [filterKey]: value }));
  };

  const hasActiveFilters =
    searchText !== "" || Object.keys(DEFAULT_FILTERS).some((key) => filters[key] !== "all");

  const onClearAllFilters = () => {
    setSearchText("");
    setFilters(DEFAULT_FILTERS);
  };

  const adminSettingsTableColumns = getAdminSettingsTableColumns(company_config?.cx_provider);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center p-1.5 px-2 bg-white rounded-lg flex-1 min-w-[280px] max-w-[480px]">
          <Input
            placeholder={t("Search a member...")}
            isClearable
            onClear={() => setSearchText("")}
            radius="full"
            value={searchText}
            startContent={
              <IoSearch
                color="#737373"
                className="w-4 h-4"
              />
            }
            className="flex-1"
            classNames={{
              inputWrapper: ["bg-white border-none rounded-md shadow-none"],
            }}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            if (disableInvitationModal) {
              return;
            }
            setIsInviteMemberModalDisplayed(true);
          }}
          disabled={disableInvitationModal}
          className={`flex items-center gap-2 px-4 h-[40px] rounded-lg text-sm font-medium text-white bg-primary transition-colors ${
            disableInvitationModal ? "opacity-60 cursor-not-allowed" : "hover:bg-primary/90"
          }`}
        >
          <LuUserPlus size={16} />
          {t("Invite a member")}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <FilterDropdown
          icon={MdOutlineWorkspacePremium}
          placeholder="All roles"
          options={roleOptions}
          value={filters.role}
          onChange={setFilter("role")}
        />
        <FilterDropdown
          icon={MdOutlineCorporateFare}
          placeholder="All departments"
          options={toOrgFilterOptions(organisations?.departments, "All departments")}
          value={filters.departmentId}
          onChange={setFilter("departmentId")}
        />
        <FilterDropdown
          icon={LuUsers}
          placeholder="All teams"
          options={toOrgFilterOptions(organisations?.teams, "All teams")}
          value={filters.teamId}
          onChange={setFilter("teamId")}
        />
        <FilterDropdown
          icon={LuMapPin}
          placeholder="All localisations"
          options={toOrgFilterOptions(organisations?.localisations, "All localisations")}
          value={filters.localisationId}
          onChange={setFilter("localisationId")}
        />
        <FilterDropdown
          icon={MdOutlineToggleOn}
          placeholder="All statuses"
          options={statusOptions}
          value={filters.status}
          onChange={setFilter("status")}
        />
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearAllFilters}
            className="text-sm text-primary hover:underline whitespace-nowrap px-2"
          >
            {t("Clear filters")}
          </button>
        )}
        <span className="ml-auto text-sm text-[#737373] whitespace-nowrap">
          {filteredOperatorsCount} {t(filteredOperatorsCount > 1 ? "members" : "member")}
        </span>
      </div>

      <PaginationTable
        items={filteredOperators}
        columns={adminSettingsTableColumns}
        emptyState={renderTableEmptyState()}
        renderCell={renderCell}
        onRowAction={() => () => {}}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        isLoading={isOperatorsLoading}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        displaySelectAll={false}
        itemNaming="Users"
        overrideTotalItems={filteredOperatorsCount}
        isClickable={false}
      />

      {isInviteMemberModalDisplayed && (
        <Modal
          isOpen={isInviteMemberModalDisplayed}
          showFooter={false}
          size="md"
          handleOnClose={() => {
            setIsInviteMemberModalDisplayed(false);
            setSelectedOperatorForInvite(null);
            fetchOperators();
          }}
        >
          <InvitationModal
            handleOnClose={() => {
              setIsInviteMemberModalDisplayed(false);
              setSelectedOperatorForInvite(null);
              fetchOperators();
            }}
            existingOperator={selectedOperatorForInvite}
            company_config={company_config}
          />
        </Modal>
      )}
    </div>
  );
};

export default MembersTab;
